import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dropdown, Form, Icon, Input } from 'semantic-ui-react'
import UsersService from '~/api/services/users'
import FileUpload from '~/components/FileUpload'
import { FormFields } from '~/components/FormFields'
import notify from '~/components/Notify'
import { StyledImageContainer, StyledImageWrapper } from '~/pages/Profile/style'
import { GlobalEl } from '~/styles/app'
import { UserEl } from './style'
import { emailRegex } from '@src/components/FormFields/constants';
import { DAIRY_USERS_PROFILE_COUNT_USER_ID, FARMER_MANAGER_ROLE_SLUG } from '~/Constants'
import Drawer from '~/components/Drawer'
import { handleErrors } from '~/utils/helpers'

const defaultImage = '/assets/default.png'

interface ICreateEditUsersProps {
  isVisible: boolean;
  userId?: string;
  handleHiddenDrawer: () => void;
  roleOptions: any[];
  countries: any[]
  farmerGroups: any[]
}

const defaultValues = {
  email: '',
  firstName: '',
  lastName: '',
  mobileNumber: '',
  mobilePrefix: '',
  phoneNumber: '',
  roleId: '',
  phonePrefix: '',
  photo: ''
}

const CreateEditUser: React.FC<ICreateEditUsersProps> = (props) => {
  const {
    isVisible,
    userId,
    handleHiddenDrawer,
    roleOptions,
    countries,
    farmerGroups
  } = props
  const { t } = useTranslation()
  const [correctEmail, setCorrectEmail] = useState(true)
  const [isBusy, setIsBusy] = useState(false)
  const { handleSubmit, control, setValue, reset, watch, formState: { isSubmitted, dirtyFields } } = useForm({
    mode: 'onSubmit'
  })

  const photoFieldUrl = watch('photo')
  const watchPhoneCodeField = watch('phonePrefix')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')
  const phoneNumber = watch('phoneNumber')
  const watchRoleField = watch('roleId')
  const watchEmail = watch('email')

  useEffect(() => {
    if (isVisible && userId && userId !== 'create') {
      fetchEditUserData()
    } else {
      reset(defaultValues)
    }
  }, [isVisible, userId])

  async function fetchEditUserData() {
    setIsBusy(true)
    try {
      const response = await UsersService.getEditUser(userId)
      reset(response.data)
      if (response.data.photo) {
        const photoResponse = await UsersService.getUserFile(userId)
        setValue('photo', photoResponse.data)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function displayErrorMobile() {
    if (isSubmitted) {
      return (!mobileNumber || mobileNumber === '') || (!wathcMobileCodeField || wathcMobileCodeField === '')
    }
    return false
  }

  function errorMobile() {
    return (!mobileNumber || mobileNumber === '') || (!wathcMobileCodeField || wathcMobileCodeField === '')
  }

  function getEmailMessageError() {
    const errorEmailRegex = emailRegex.test(watchEmail)
    const errorMessage = !errorEmailRegex ? t('Please enter valid email') : t('Email already used')
    return <UserEl.StyledLabel basic pointing={'above'}>{errorMessage}</UserEl.StyledLabel>
  }

  function renderProfile() {
    const roleSelected = roleOptions.find(role => role.id === watchRoleField)
    return <>
      <div style={{ marginBottom: '1em' }}>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
          mimeTypes={['bmp', 'jpg', 'jpeg', 'png']}
          sizeLimitInMB={5}
        />
        }
      </div>
      <FormFields.Input
        control={control}
        label={t('First name')}
        disableScroolIntoView
        rules={{
          required: {
            value: true,
            message: t('Please enter first name')
          }
        }}
        name={'firstName'}
        placeholder={t('First name')}
      />
      <FormFields.Input
        control={control}
        label={t('Last name')}
        disableScroolIntoView
        rules={{
          required: {
            value: true,
            message: t('Please enter last name')
          }
        }}
        name={'lastName'}
        placeholder={t('Last name')}
      />
      <FormFields.Select
        options={roleOptions?.map(role => ({ value: role.id, text: role.label }))}
        selection
        selectOnBlur={false}
        disableScroolIntoView
        rules={{
          required: {
            value: true,
            message: t('Please enter role')
          }
        }}
        placeholder={t('Select role')}
        control={control}
        label={t('Role')}
        name={'roleId'}
      />
      {roleSelected?.slug === FARMER_MANAGER_ROLE_SLUG && <FormFields.Select
        options={farmerGroups}
        selection
        multiple
        search
        selectOnBlur={false}
        disableScroolIntoView
        rules={{
          required: {
            value: true,
            message: t('Please select farmer group')
          }
        }}
        placeholder={t('Select farmer group')}
        control={control}
        label={t('Farmer group')}
        name={'farmerGroups'}
      />}
      <FormFields.Input
        control={control}
        disableScroolIntoView
        label={t('Email')}
        rules={{
          required: {
            value: true,
            message: t('Please enter email')
          },
          pattern: {
            value: emailRegex,
            message: t('Please enter valid email')
          }
        }}
        typeInput={'emailField'}
        handleEmailCheck={(correctEmail) => setCorrectEmail(correctEmail)}
        name={'email'}
        placeholder={t('Email')}
      />
      {!correctEmail && getEmailMessageError()}
      <Form.Field style={{ paddingLeft: 0 }} >
        <label>{t('Phone number')}</label>
        <Input
          style={{ minWidth: 250 }}
          value={phoneNumber || ''}
          label={<Dropdown
            size='small'
            options={countries?.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
            selectOnBlur={false}
            basic
            clearable
            scrolling
            value={parseInt(watchPhoneCodeField) || ''}
            placeholder={t('Code')}
            onChange={(e, data) => setValue('phonePrefix', data.value)}
            name={'phonePrefix'}
          />}
          onChange={(e, data) => setValue('phoneNumber', data.value)}
          labelPosition='left'
          type={'number'}
          placeholder={watchPhoneCodeField ? t('Phone number') : t('Please select phone code')}
        />
      </Form.Field>
      <Form.Field style={{ marginBottom: 10, paddingLeft: 0 }} required>
        <label style={{ display: 'flex', alignItems: 'baseline' }}>{t('Mobile number')}</label>
        <Input
          style={{ minWidth: 250 }}
          value={mobileNumber || ''}
          label={<Dropdown
            scrolling
            basic
            options={countries?.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
            selectOnBlur={false}
            onChange={(e, data) => setValue('mobilePrefix', data.value)}
            value={parseInt(wathcMobileCodeField) || ''}
            placeholder={t('Code')}
            name={'mobilePrefix'}
          />}
          onChange={(e, data) => setValue('mobileNumber', data.value)}
          labelPosition='left'
          placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
          type={'number'}
          name={'mobileNumber'}
        />
        {displayErrorMobile() && <UserEl.StyledLabel basic pointing={'above'}>{t('Please enter mobile number and mobile code')}</UserEl.StyledLabel>}
      </Form.Field>
      <UserEl.DrawerFooter>
        <GlobalEl.PrimaryButton
          disabled={!correctEmail}>
          {userId !== 'create' ? t('Update') : t('Add')}
        </GlobalEl.PrimaryButton>
      </UserEl.DrawerFooter>
    </>
  }

  async function onSubmit(data) {
    if (!errorMobile()) {
      setIsBusy(true)
      try {
        if (userId !== 'create') {
          await UsersService.updateUser(userId, data)
          notify(t('User updated'), 'success');
        } else {
          await UsersService.storeUser(data)
          const usersElement = document.getElementById(DAIRY_USERS_PROFILE_COUNT_USER_ID)
          usersElement.innerText = (parseInt(usersElement.innerText) + 1).toString()
          notify(t('User created'), 'success');
        }
        setCorrectEmail(true)
        handleHiddenDrawer()
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  return <Drawer
    content={isVisible && !isBusy && renderProfile()}
    header={userId !== 'create' ? t('Edit user') : t('Add New User')}
    onHide={() => {
      setCorrectEmail(true)
      //   setUpdateLogsData(null);
      reset();
      handleHiddenDrawer();
    }}
    isBusy={isBusy}
    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    closeOnEscape
  />
}
export default CreateEditUser
